/* AVERELLE cart controls (own code, 2 Nov 2026).
   Works for every element with [data-avr-cart="<section id>"] – the cart drawer
   (snippets/cart-drawer.liquid → section "cart-drawer-section") and the cart page
   (sections/avr-main-cart). Uses Shopify's Ajax Cart API with section rendering:
   - quantity +/- / typed quantity / "Entfernen": POST /cart/change.js { id, quantity, sections }
   - discount codes: POST /cart/update.js { discount, sections }
   - shipping estimate (cart page): GET /cart/shipping_rates.json
   After every change the returned section HTML replaces the affected cart markup and a standard
   cart event is dispatched so the header cart count (and the theme's drawer logic) stay in sync. */

const ROOT_SELECTOR = '[data-avr-cart]';
let busy = false;

/** @returns {HTMLElement[]} */
const roots = () => Array.from(document.querySelectorAll(ROOT_SELECTOR));

const sectionIds = () => Array.from(new Set(roots().map((el) => el.dataset.avrCart).filter(Boolean)));

let eventsModule;
const loadEvents = async () => {
  if (eventsModule !== undefined) return eventsModule;
  try {
    eventsModule = await import('@shopify/events');
  } catch (error) {
    eventsModule = null;
  }
  return eventsModule;
};

const setBusy = (state) => {
  busy = state;
  roots().forEach((root) => root.classList.toggle('avr-cart-busy', state));
};

/** Replace each cart root with the freshly rendered markup of its section. */
const renderSections = (sections) => {
  if (!sections) return;
  for (const root of roots()) {
    const html = sections[root.dataset.avrCart];
    if (!html) continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(`[data-avr-cart="${CSS.escape(root.dataset.avrCart)}"]`);
    if (!fresh) continue;
    root.className = fresh.className;
    root.innerHTML = fresh.innerHTML;
  }
  if (window.__avrXsell && typeof window.__avrXsell.scan === 'function') window.__avrXsell.scan();
  document.querySelectorAll('[data-avr-ship]').forEach((box) => setupProvinces(box));
};

/** Tell the rest of the theme (header count, drawer state) that the cart lines changed. */
const announce = async (cart, action, lines) => {
  const events = await loadEvents();
  const count = Number(cart && cart.item_count) || 0;
  if (events && events.CartLinesUpdateEvent && typeof events.CartLinesUpdateEvent.createPromise === 'function') {
    try {
      const deferred = events.CartLinesUpdateEvent.createPromise();
      const target = document.querySelector('theme-drawer#cart-drawer') || document;
      target.dispatchEvent(
        new events.CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise })
      );
      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: cart.sections || undefined, items: cart.items, itemCount: count, source: 'avr-cart', didError: false },
      });
      return;
    } catch (error) {
      /* fall through to the direct update below */
    }
  }
  document.querySelectorAll('cart-icon').forEach((icon) => {
    if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(count, false);
  });
};

const post = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: sectionIds().join(','), sections_url: window.location.pathname }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status >= 400 || data.errors) {
    const message = data.description || data.message || (typeof data.errors === 'string' ? data.errors : '') || 'Error';
    throw Object.assign(new Error(message), { data });
  }
  return data;
};

const showLineError = (line, message) => {
  const box = line && line.querySelector('.avr-cline__error');
  if (!box) return;
  box.textContent = message;
  box.hidden = false;
};

const changeLine = async (line, quantity) => {
  if (busy || !line) return;
  const key = line.dataset.avrLine;
  setBusy(true);
  try {
    const cart = await post(routeUrl('cart_change_url', '/cart/change') + '.js', { id: key, quantity });
    renderSections(cart.sections);
    announce(cart, quantity === 0 ? 'remove' : 'update', [{ id: key, quantity }]);
  } catch (error) {
    showLineError(line, error.message);
    const input = line.querySelector('[data-avr-qty-input]');
    if (input) input.value = input.defaultValue;
  } finally {
    setBusy(false);
  }
};

const currentCodes = (root) =>
  Array.from(root.querySelectorAll('[data-avr-discount-remove]')).map((button) => button.dataset.avrDiscountRemove);

const updateDiscounts = async (root, codes, errorBox) => {
  if (busy) return;
  setBusy(true);
  try {
    const cart = await post(routeUrl('cart_update_url', '/cart/update') + '.js', { discount: codes.join(',') });
    renderSections(cart.sections);
    const added = codes[codes.length - 1];
    const applied = (cart.discount_codes || []).find((entry) => entry.code && added && entry.code.toLowerCase() === added.toLowerCase());
    if (added && applied && applied.applicable === false) {
      roots().forEach((el) => el.querySelectorAll('.avr-discount__error').forEach((box) => (box.hidden = false)));
    }
    announce(cart, 'update', []);
  } catch (error) {
    if (errorBox) errorBox.hidden = false;
  } finally {
    setBusy(false);
  }
};

const routeUrl = (name, fallback) => {
  const routes = (window.Theme && window.Theme.routes) || (window.Shopify && window.Shopify.routes) || {};
  const value = routes[name];
  if (value) return value.replace(/\.js$/, '');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.replace(/\/$/, '') + fallback;
};

/* ---------- events (delegated, survive re-rendering) ---------- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const qty = target.closest('[data-avr-qty]');
  if (qty && qty.closest(ROOT_SELECTOR)) {
    event.preventDefault();
    const quantity = Math.max(0, parseInt(qty.dataset.avrQty, 10) || 0);
    changeLine(qty.closest('[data-avr-line]'), quantity);
    return;
  }

  const remove = target.closest('[data-avr-discount-remove]');
  if (remove && remove.closest(ROOT_SELECTOR)) {
    event.preventDefault();
    const root = remove.closest('[data-avr-discount]');
    const codes = currentCodes(root).filter((code) => code !== remove.dataset.avrDiscountRemove);
    updateDiscounts(root, codes, root.querySelector('.avr-discount__error'));
    return;
  }

  const ship = target.closest('[data-avr-ship-submit]');
  if (ship) {
    event.preventDefault();
    estimate(ship.closest('[data-avr-ship]'));
  }
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-avr-qty-input]')) return;
  const quantity = Math.max(0, parseInt(input.value, 10) || 0);
  if (String(quantity) === input.defaultValue) return;
  changeLine(input.closest('[data-avr-line]'), quantity);
});

document.addEventListener('keydown', (event) => {
  const input = event.target;
  if (event.key !== 'Enter' || !(input instanceof HTMLInputElement)) return;

  if (input.matches('[data-avr-qty-input]')) {
    event.preventDefault();
    input.blur();
    return;
  }

  if (input.matches('[data-avr-discount-input]')) {
    event.preventDefault();
    const code = input.value.trim();
    if (!code) return;
    const root = input.closest('[data-avr-discount]');
    const codes = currentCodes(root).filter((existing) => existing.toLowerCase() !== code.toLowerCase());
    codes.push(code);
    updateDiscounts(root, codes, root.querySelector('.avr-discount__error'));
    return;
  }

  if (input.matches('[data-avr-ship-zip]')) {
    event.preventDefault();
    estimate(input.closest('[data-avr-ship]'));
  }
});

/* ---------- shipping estimator (cart page) ---------- */

const formatMoney = (amount) => {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency }).format(Number(amount));
  } catch (error) {
    return `${amount} ${currency}`;
  }
};

const escapeHtml = (value) => {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
};

const setupProvinces = (box) => {
  if (!box || box.dataset.avrReady) return;
  box.dataset.avrReady = '1';
  const country = box.querySelector('[data-avr-ship-country]');
  const province = box.querySelector('[data-avr-ship-province]');
  const wrap = box.querySelector('[data-avr-ship-province-wrap]');
  if (!country) return;
  const wanted = country.dataset.default;
  if (wanted) {
    const match = Array.from(country.options).find((option) => option.value === wanted || option.textContent.trim() === wanted);
    if (match) country.value = match.value;
  }
  const sync = () => {
    const option = country.options[country.selectedIndex];
    let list = [];
    try {
      list = JSON.parse((option && option.dataset.provinces) || '[]');
    } catch (error) {
      list = [];
    }
    province.innerHTML = list.map(([value, label]) => `<option value="${escapeHtml(value)}">${escapeHtml(label)}</option>`).join('');
    wrap.hidden = list.length === 0;
  };
  country.addEventListener('change', sync);
  sync();
};

const estimate = async (box) => {
  if (!box) return;
  const result = box.querySelector('[data-avr-ship-result]');
  const country = box.querySelector('[data-avr-ship-country]').value;
  const provinceWrap = box.querySelector('[data-avr-ship-province-wrap]');
  const province = provinceWrap && !provinceWrap.hidden ? box.querySelector('[data-avr-ship-province]').value : '';
  const zip = box.querySelector('[data-avr-ship-zip]').value.trim();
  const params = new URLSearchParams();
  params.set('shipping_address[country]', country);
  params.set('shipping_address[province]', province);
  params.set('shipping_address[zip]', zip);
  result.hidden = false;
  result.classList.remove('is-error');
  result.innerHTML = '…';
  try {
    const response = await fetch(`${routeUrl('cart_url', '/cart')}/shipping_rates.json?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    const data = await response.json();
    if (!response.ok) {
      const messages = Object.entries(data || {})
        .map(([field, errors]) => `${field}: ${[].concat(errors).join(', ')}`)
        .join('<br>');
      result.classList.add('is-error');
      result.innerHTML = `<p>${escapeHtml(result.dataset.error)}</p><p>${messages}</p>`;
      return;
    }
    const rates = data.shipping_rates || [];
    if (!rates.length) {
      result.innerHTML = `<p>${escapeHtml(result.dataset.none)}</p>`;
      return;
    }
    const intro = rates.length === 1 ? result.dataset.one : result.dataset.many;
    const items = rates
      .map((rate) => {
        const price = Number(rate.price) === 0 ? result.dataset.free : formatMoney(rate.price);
        return `<li>${escapeHtml(rate.name)}: ${escapeHtml(price)}</li>`;
      })
      .join('');
    result.innerHTML = `<p>${escapeHtml(intro)}</p><ul role="list">${items}</ul>`;
  } catch (error) {
    result.classList.add('is-error');
    result.innerHTML = `<p>${escapeHtml(result.dataset.error)}</p>`;
  }
};

const init = () => document.querySelectorAll('[data-avr-ship]').forEach(setupProvinces);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
